/* components/patterns/chat/WaveformRecorder.jsx — визуализация записи голоса (S5).
   Canvas-волна (rAF, фейковая синусоида), таймер MM:SS, иконо-кнопка «пауза» (завершает запись),
   предупреждение при ≥150 с (фикс боли №31), overlay «Распознаю…» при транскрипции.
   Таймер ведёт Composer (setInterval — надёжно в iframe); здесь только отрисовка.
   Источник: 06 §2.6a. ХЕНДОФФ: → frontend; AnalyserNode вместо синусоиды. */

import React from 'react';
import { Button } from '../../ui/Button.jsx';
import { Icon } from '../../ui/Icon.jsx';

  const { useRef, useEffect } = React;
  const MAX = 180;

  function mmss(s) {
    const m = Math.floor(s / 60), ss = s % 60;
    return `${m}:${String(ss).padStart(2, '0')}`;
  }

  // акцентный цвет для canvas (нужен литерал): из CSS-токена --accent, иначе из словаря
  // window.RAW_TOKENS.accent, иначе из вычисленного currentColor элемента — без хардкода hex.
  function resolveAccent(el) {
    const fromToken = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim();
    if (fromToken) return fromToken;
    if (typeof window !== 'undefined' && window.RAW_TOKENS && window.RAW_TOKENS.accent) return window.RAW_TOKENS.accent;
    if (el) { const c = getComputedStyle(el).color; if (c) return c; }
    return 'currentColor';
  }
  const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;

  function WaveCanvas({ active }) {
    const ref = useRef(null);
    const raf = useRef(null);
    useEffect(() => {
      const cv = ref.current; if (!cv) return;
      const ctx = cv.getContext('2d');
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      let t = 0;
      const bars = 40;
      const reduce = prefersReducedMotion();
      function resize() {
        const w = cv.clientWidth, h = cv.clientHeight;
        cv.width = w * dpr; cv.height = h * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        if (reduce) draw(); // при reduce перерисовываем статичную волну под новый размер
      }
      const accent = resolveAccent(cv);
      function draw() {
        const w = cv.clientWidth, h = cv.clientHeight;
        ctx.clearRect(0, 0, w, h);
        const gap = w / bars;
        for (let i = 0; i < bars; i++) {
          // фейковая «живая» волна: сумма синусов + затухание к краям.
          // при reduce фаза t фиксирована (t=0) → статичный, неподвижный профиль.
          const env = Math.sin((i / bars) * Math.PI);
          const amp = (0.35 + 0.65 * Math.abs(Math.sin(t * 0.08 + i * 0.5) * Math.cos(t * 0.05 + i * 0.3)));
          const bh = Math.max(3, env * amp * (h * 0.8));
          const x = i * gap + gap * 0.25;
          ctx.fillStyle = accent;
          ctx.globalAlpha = 0.35 + 0.5 * env;
          const r = Math.min(gap * 0.25, 2);
          const y = (h - bh) / 2;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(x, y, gap * 0.5, bh, r); else ctx.rect(x, y, gap * 0.5, bh);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        if (reduce) return;        // reduced-motion: один статичный кадр, без rAF-петли
        t += 1;
        raf.current = requestAnimationFrame(draw);
      }
      resize();
      if (!reduce) draw();         // в обычном режиме запускаем анимацию (resize рисует статичный кадр сам)
      window.addEventListener('resize', resize);
      return () => { if (raf.current) cancelAnimationFrame(raf.current); window.removeEventListener('resize', resize); };
    }, []);
    return <canvas ref={ref} className="wave-rec__canvas" />;
  }

  function WaveformRecorder({ recState, recSeconds = 0, onStop }) {
    const transcribing = recState === 'transcribing';
    const remain = MAX - recSeconds;
    const warn = recState === 'recording' && recSeconds >= 150;

    return (
      <div className="wave-rec" role="group" aria-label="Запись голоса">
        <span className="wave-rec__dot" aria-hidden="true" />
        <span className="wave-rec__time num">{mmss(recSeconds)}</span>
        <div className="wave-rec__wave"><WaveCanvas active={recState === 'recording'} /></div>
        {warn && (
          <span className="wave-rec__warn t-caption" role="status">
            <Icon name="clock" size={12} /> Осталось {Math.max(0, remain)} с
          </span>
        )}
        <Button variant="primary" size="sm" iconOnly="pause" onClick={onStop} disabled={transcribing}
                aria-label="Завершить запись" title="Завершить запись" />
        {transcribing && (
          <div className="wave-rec__overlay">
            <Icon name="spinner" size={16} /> <span>Распознаю…</span>
          </div>
        )}
      </div>
    );
  }

export { WaveformRecorder };
